'use client';

import { useState, useMemo } from 'react';
import { useTranslations } from 'next-intl';
import { useQuery } from '@tanstack/react-query';
import QRCode from 'react-qr-code';
import { toast } from 'sonner';
import { IconPrinter } from '@tabler/icons-react';

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { PrintPortal } from '@/components/print-portal';

import { registrationQueries } from '@/features/registration';
import { useCreatePrintBatchMutation } from '@/features/sticking';
import {
  useLabelPrintConfig,
  useActiveZebraPrinterUid,
  useZebraPrinterProfiles
} from '@/features/configurations';
import { printLabels } from '@/lib/zebra';

import {
  DEFAULT_STICKER_HEIGHT_MM,
  DEFAULT_STICKER_WIDTH_MM
} from '../constants';
import { printWithPageSize } from '../lib/print-helpers';
import type { PrintLogEntry, PrinterMode } from '../types';

const LOG_LIMIT = 10;

interface StickerProps {
  containerNumber: string;
  pageNumber: number;
  widthMm: number;
  heightMm: number;
}

function StickerLabel({
  containerNumber,
  pageNumber,
  widthMm,
  heightMm
}: StickerProps) {
  // Inline styles mirror the @media print rules in globals.css so the on-screen
  // preview matches the printed output without depending on print-only CSS.
  return (
    <div
      className='qr-sticker'
      style={{
        width: `${widthMm}mm`,
        height: `${heightMm}mm`,
        padding: `${Math.max(0.3, widthMm * 0.03)}mm`,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        boxSizing: 'border-box',
        overflow: 'hidden',
        pageBreakInside: 'avoid',
        breakInside: 'avoid'
      }}
    >
      <p
        style={{
          margin: 0,
          marginBottom: '0.5px',
          fontSize: `${Math.max(2, widthMm * 0.35)}px`,
          fontWeight: 700,
          textAlign: 'center',
          lineHeight: 1,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          maxWidth: '100%'
        }}
      >
        {containerNumber}
      </p>
      <div
        style={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%'
        }}
      >
        <QRCode
          value={`${containerNumber}-P${pageNumber}`}
          size={Math.max(20, widthMm * 2.8)}
          level='M'
          style={{ width: '100%', height: '100%' }}
        />
      </div>
      <p
        style={{
          margin: 0,
          marginTop: '0.5px',
          fontSize: `${Math.max(2, widthMm * 0.35)}px`,
          fontWeight: 700,
          textAlign: 'center',
          lineHeight: 1
        }}
      >
        P-{pageNumber}
      </p>
    </div>
  );
}

export function PrinterSimulator() {
  const t = useTranslations('devTools.printer');
  const { labelWidthMm, labelHeightMm, gapMm, labelDpi } =
    useLabelPrintConfig();
  const printerUid = useActiveZebraPrinterUid();
  const profiles = useZebraPrinterProfiles();

  const [mode, setMode] = useState<PrinterMode>('grid');
  const [registrationId, setRegistrationId] = useState<number | null>(null);
  const [pageNumber, setPageNumber] = useState<string>('1');
  const [startPage, setStartPage] = useState<string>('1');
  const [pageCount, setPageCount] = useState<string>('33');
  const [stickerWidth, setStickerWidth] = useState<number>(
    DEFAULT_STICKER_WIDTH_MM
  );
  const [stickerHeight, setStickerHeight] = useState<number>(
    DEFAULT_STICKER_HEIGHT_MM
  );
  const [createBatch, setCreateBatch] = useState<boolean>(true);
  const [log, setLog] = useState<PrintLogEntry[]>([]);

  const { data: regsData, isLoading } = useQuery(
    registrationQueries.list({
      per_page: 30,
      sort_by: 'created_at',
      sort_direction: 'desc'
    })
  );
  const registrations = regsData?.data ?? [];
  const selectedReg = registrations.find((r) => r.id === registrationId);
  const containerNumber = selectedReg?.container_number ?? '';

  const printMutation = useCreatePrintBatchMutation(registrationId ?? 0);

  const gridPages = useMemo(() => {
    const start = Number(startPage) || 1;
    const count = Number(pageCount) || 0;
    return Array.from({ length: count }, (_, i) => start + i);
  }, [startPage, pageCount]);

  const handlePrint = async () => {
    if (!registrationId || !containerNumber) {
      toast.error(t('missingFieldsToast'));
      return;
    }

    if (createBatch) {
      try {
        const start =
          mode === 'single' ? Number(pageNumber) || 1 : Number(startPage) || 1;
        const count = mode === 'single' ? 1 : Number(pageCount) || 1;
        await printMutation.mutateAsync({
          start_page: start,
          page_count: count
        });
        toast.success(t('batchCreatedToast'));
      } catch {
        return;
      }
    }

    if (printerUid) {
      const labels =
        mode === 'single'
          ? [
              {
                headerText: containerNumber,
                qrPayload: `${containerNumber}-P${Number(pageNumber) || 1}`,
                footerText: `P-${Number(pageNumber) || 1}`
              }
            ]
          : gridPages.map((p) => ({
              headerText: containerNumber,
              qrPayload: `${containerNumber}-P${p}`,
              footerText: `P-${p}`
            }));
      const result = await printLabels({
        printerUid,
        labels,
        profiles,
        fallbackOptions: {
          widthMm: mode === 'single' ? stickerWidth : labelWidthMm,
          heightMm: mode === 'single' ? stickerHeight : labelHeightMm,
          dpi: labelDpi
        }
      });
      if (result.ok) {
        toast.success(
          mode === 'single'
            ? t('successToastSingle')
            : t('successToastGrid', { pages: gridPages.length })
        );
      } else {
        toast.error(t('zebraPrintFailed', { reason: result.error }));
      }
    } else if (mode === 'single') {
      await printWithPageSize(stickerWidth, stickerHeight, 1);
      toast.success(t('successToastSingle'));
    } else {
      window.print();
      toast.success(t('successToastGrid', { pages: gridPages.length }));
    }

    const start =
      mode === 'single' ? Number(pageNumber) || 1 : Number(startPage) || 1;
    const count = mode === 'single' ? 1 : Number(pageCount) || 1;

    setLog((prev) =>
      [
        {
          id: `${Date.now()}`,
          mode,
          registrationId,
          startPage: start,
          pageCount: count,
          createdAt: new Date().toISOString()
        },
        ...prev
      ].slice(0, LOG_LIMIT)
    );
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('title')}</CardTitle>
        <CardDescription>{t('description')}</CardDescription>
      </CardHeader>
      <CardContent className='flex flex-col gap-6'>
        <div className='space-y-2'>
          <Label>{t('modeLabel')}</Label>
          <RadioGroup
            value={mode}
            onValueChange={(value) => setMode(value as PrinterMode)}
            className='flex flex-col gap-2 sm:flex-row sm:gap-6'
          >
            <div className='flex items-start gap-2'>
              <RadioGroupItem id='dev-tools-mode-single' value='single' />
              <div>
                <Label
                  htmlFor='dev-tools-mode-single'
                  className='cursor-pointer'
                >
                  {t('modeSingle')}
                </Label>
                <p className='text-muted-foreground text-xs'>
                  {t('modeSingleHint')}
                </p>
              </div>
            </div>
            <div className='flex items-start gap-2'>
              <RadioGroupItem id='dev-tools-mode-grid' value='grid' />
              <div>
                <Label htmlFor='dev-tools-mode-grid' className='cursor-pointer'>
                  {t('modeGrid')}
                </Label>
                <p className='text-muted-foreground text-xs'>
                  {t('modeGridHint')}
                </p>
              </div>
            </div>
          </RadioGroup>
        </div>

        <div className='grid gap-4 md:grid-cols-2'>
          <div className='space-y-2'>
            <Label htmlFor='dev-tools-reg'>
              {t('selectRegistrationLabel')}
            </Label>
            <Select
              value={registrationId ? String(registrationId) : ''}
              onValueChange={(value) => setRegistrationId(Number(value))}
              disabled={isLoading}
            >
              <SelectTrigger id='dev-tools-reg'>
                <SelectValue placeholder={t('selectRegistrationPlaceholder')} />
              </SelectTrigger>
              <SelectContent>
                {registrations.length === 0 ? (
                  <div className='text-muted-foreground px-3 py-2 text-sm'>
                    {t('selectRegistrationEmpty')}
                  </div>
                ) : (
                  registrations.map((r) => (
                    <SelectItem key={r.id} value={String(r.id)}>
                      #{r.id} · {r.container_number}
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
          </div>

          {mode === 'single' ? (
            <>
              <div className='space-y-2'>
                <Label htmlFor='dev-tools-page'>{t('pageNumberLabel')}</Label>
                <Input
                  id='dev-tools-page'
                  type='number'
                  min={1}
                  value={pageNumber}
                  onChange={(e) => setPageNumber(e.target.value)}
                />
              </div>
              <div className='space-y-2'>
                <Label htmlFor='dev-tools-stickerW'>
                  {t('stickerWidthLabel')}
                </Label>
                <Input
                  id='dev-tools-stickerW'
                  type='number'
                  min={5}
                  max={200}
                  step='0.1'
                  value={stickerWidth}
                  onChange={(e) => setStickerWidth(Number(e.target.value))}
                />
              </div>
              <div className='space-y-2'>
                <Label htmlFor='dev-tools-stickerH'>
                  {t('stickerHeightLabel')}
                </Label>
                <Input
                  id='dev-tools-stickerH'
                  type='number'
                  min={5}
                  max={200}
                  step='0.1'
                  value={stickerHeight}
                  onChange={(e) => setStickerHeight(Number(e.target.value))}
                />
              </div>
            </>
          ) : (
            <>
              <div className='space-y-2'>
                <Label htmlFor='dev-tools-start'>{t('startPageLabel')}</Label>
                <Input
                  id='dev-tools-start'
                  type='number'
                  min={1}
                  value={startPage}
                  onChange={(e) => setStartPage(e.target.value)}
                />
              </div>
              <div className='space-y-2'>
                <Label htmlFor='dev-tools-count'>{t('pageCountLabel')}</Label>
                <Input
                  id='dev-tools-count'
                  type='number'
                  min={1}
                  max={500}
                  value={pageCount}
                  onChange={(e) => setPageCount(e.target.value)}
                />
              </div>
            </>
          )}
        </div>

        <div className='flex items-start gap-2 rounded-md border p-4'>
          <Switch
            id='dev-tools-create-batch'
            checked={createBatch}
            onCheckedChange={setCreateBatch}
          />
          <div className='flex-1'>
            <Label htmlFor='dev-tools-create-batch' className='cursor-pointer'>
              {t('createBatchToggle')}
            </Label>
            <p className='text-muted-foreground text-xs'>
              {t('createBatchHint')}
            </p>
          </div>
        </div>

        <div>
          <Button
            onClick={handlePrint}
            disabled={printMutation.isPending || !registrationId}
          >
            <IconPrinter className='size-4' />
            {t('printButton')}
          </Button>
        </div>

        <div className='space-y-2'>
          <h4 className='text-sm font-medium'>{t('previewHeader')}</h4>
          <p className='text-muted-foreground text-xs'>
            {mode === 'single' ? t('previewSingleNote') : t('previewGridNote')}
          </p>
          <div className='bg-muted flex items-center justify-center overflow-auto rounded-md border p-6'>
            {!containerNumber ? (
              <p className='text-muted-foreground text-sm'>
                {t('previewEmpty')}
              </p>
            ) : mode === 'single' ? (
              <div className='bg-white shadow-sm'>
                <StickerLabel
                  containerNumber={containerNumber}
                  pageNumber={Number(pageNumber) || 1}
                  widthMm={stickerWidth}
                  heightMm={stickerHeight}
                />
              </div>
            ) : (
              <div
                className='qr-sticker-grid bg-white p-2 shadow-sm'
                style={{
                  display: 'grid',
                  gridTemplateColumns: `repeat(auto-fill, ${labelWidthMm}mm)`,
                  gap: `${gapMm}mm`,
                  width: '210mm',
                  minHeight: '60mm'
                }}
              >
                {gridPages.map((p) => (
                  <StickerLabel
                    key={p}
                    containerNumber={containerNumber}
                    pageNumber={p}
                    widthMm={labelWidthMm}
                    heightMm={labelHeightMm}
                  />
                ))}
              </div>
            )}
          </div>
        </div>

        <div className='space-y-2'>
          <h4 className='text-sm font-medium'>{t('logHeader')}</h4>
          {log.length === 0 ? (
            <p className='text-muted-foreground text-sm'>{t('logEmpty')}</p>
          ) : (
            <div className='rounded-md border'>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('logColumns.mode')}</TableHead>
                    <TableHead>{t('logColumns.registration')}</TableHead>
                    <TableHead>{t('logColumns.range')}</TableHead>
                    <TableHead>{t('logColumns.createdAt')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {log.map((entry) => (
                    <TableRow key={entry.id}>
                      <TableCell>
                        <Badge variant='outline' className='text-xs uppercase'>
                          {entry.mode}
                        </Badge>
                      </TableCell>
                      <TableCell className='font-mono text-xs'>
                        #{entry.registrationId}
                      </TableCell>
                      <TableCell className='font-mono text-xs'>
                        {entry.pageCount === 1
                          ? `P-${entry.startPage}`
                          : `${entry.startPage} – ${entry.startPage + entry.pageCount - 1}`}
                      </TableCell>
                      <TableCell className='text-muted-foreground text-xs'>
                        {new Date(entry.createdAt).toLocaleTimeString()}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      </CardContent>

      {/* Print-only zone — hidden until window.print() fires */}
      {containerNumber && (
        <PrintPortal>
          <div className='hidden' data-print-zone>
            {mode === 'single' ? (
              <StickerLabel
                containerNumber={containerNumber}
                pageNumber={Number(pageNumber) || 1}
                widthMm={stickerWidth}
                heightMm={stickerHeight}
              />
            ) : (
              <div
                className='qr-sticker-grid'
                style={{
                  gridTemplateColumns: `repeat(auto-fill, ${labelWidthMm}mm)`,
                  gap: `${gapMm}mm`
                }}
              >
                {gridPages.map((p) => (
                  <StickerLabel
                    key={p}
                    containerNumber={containerNumber}
                    pageNumber={p}
                    widthMm={labelWidthMm}
                    heightMm={labelHeightMm}
                  />
                ))}
              </div>
            )}
          </div>
        </PrintPortal>
      )}
    </Card>
  );
}
